import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import {
  Avatar,
  EmptyCell,
  EmptyState,
  type IColumn,
  IconButton,
  type IRow,
  NameValueRecord,
  Table,
  Tooltip,
  Typography,
} from 'shared/ui'
import { getLabelAsNumberInternationalFormat } from 'shared/lib'
import {
  type ChatbotAnalyticListItem,
  ChatbotConversationStatus,
  type IChatbotConversationStatus,
} from 'entities/Chatbot'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'

import {
  type ChatbotAnalyticDetailStore,
  type ChatbotAnalyticListStore,
} from 'pages/chatbot/pages/chatbotAnalytic/store'
import styles from './styles.module.scss'
import { unEnrollActions } from './actions/unEnrollActions'

const getTimeRowName = (status: IChatbotConversationStatus | null) => {
  switch (status) {
    case ChatbotConversationStatus.Abandoned:
      return 'Abandoned time'
    case ChatbotConversationStatus.Completed:
      return 'Completed time'
    case ChatbotConversationStatus.Terminated:
    default:
      return 'Send time'
  }
}

const getTimeRowValue = (
  status: IChatbotConversationStatus | null,
  row: IRow<ChatbotAnalyticListItem>
) => {
  switch (status) {
    case ChatbotConversationStatus.Abandoned:
    case ChatbotConversationStatus.Completed:
    case ChatbotConversationStatus.QuietHours:
      return row.targetTime
    case ChatbotConversationStatus.Terminated:
    default:
      return row.createdTime
  }
}

const useColumns = (
  status: IChatbotConversationStatus | null,
  onOpenContactsDetails: IOpenContactsDetails,
  details: ChatbotAnalyticDetailStore,
  isQuietHoursView: boolean
) =>
  useMemo(() => {
    const nameColumn: IColumn<ChatbotAnalyticListItem> = {
      field: 'contact',
      name: 'Name',
      renderRowCell: (row, _, config) => {
        const { contact, sendFrom } = row
        if (!contact) return null

        return (
          <div className={styles.tableContact}>
            <div className={styles.tableContactName}>
              <Avatar info={contact.avatarInfo} size={24} />
              <Typography
                variant={'body-md-regular'}
                ellipsis
                tooltipProps={{ label: contact.name }}
                ariaLabel={'displayInfo'}
              >
                {contact.full_name || '—'}
              </Typography>
            </div>
            <Tooltip label='Conversation' placement='top'>
              <IconButton
                icon='conversation'
                variant='icon'
                color='tertiary'
                transparentInactive
                className={config?.hidden}
                disabled={onOpenContactsDetails.disabled}
                onClick={(event) => {
                  event.stopPropagation()
                  onOpenContactsDetails.open({
                    contactId: contact.id,
                    inboxId: sendFrom?.options.team_id,
                    numberId: sendFrom?.options.number_id ?? undefined,
                  })
                }}
                ariaLabel='ContactRow_sendMessage'
              />
            </Tooltip>
          </div>
        )
      },
    }

    const numberColumn: IColumn<ChatbotAnalyticListItem> = {
      field: 'number',
      name: 'Phone',
      renderRowCell: ({ contact }) =>
        (contact?.number && getLabelAsNumberInternationalFormat(contact.number)) ?? null,
    }

    const emailColumn: IColumn<ChatbotAnalyticListItem> = {
      field: 'email',
      name: 'Email',
      renderRowCell: ({ contact }) => contact?.email ?? null,
    }

    const targetTimeColumn: IColumn<ChatbotAnalyticListItem> = {
      field: 'sendTime',
      name: getTimeRowName(status),
      renderRowCell: (row) => getTimeRowValue(status, row),
    }

    const sendFromColumn: IColumn<ChatbotAnalyticListItem> = {
      field: 'sendFrom',
      name: 'Send from',
      renderRowCell: (row) => {
        const sendFromInfo = row.sendFrom ? details.getSendFromInfoSync(row.sendFrom) : null

        return sendFromInfo ? (
          <NameValueRecord icon='inboxes2' name={sendFromInfo.name} value={sendFromInfo.value} />
        ) : (
          <EmptyCell />
        )
      },
    }

    return [
      nameColumn,
      numberColumn,
      emailColumn,
      targetTimeColumn,
      ...(isQuietHoursView ? [] : [sendFromColumn]),
    ]
  }, [status, onOpenContactsDetails.disabled, details, isQuietHoursView])

export interface IChatbotAnalyticTableProps {
  details: ChatbotAnalyticDetailStore
  list: ChatbotAnalyticListStore
}

export const ChatbotAnalyticTable = observer(({ list, details }: IChatbotAnalyticTableProps) => {
  const {
    onOpenContactsDetails,
    items,
    activeStatus,
    page,
    limit,
    total,
    isEmpty,
    loading,
    loadingScheduled,
    changePaging,
    isQuietHoursView,
  } = list

  const columns = useColumns(activeStatus, onOpenContactsDetails, details, isQuietHoursView)
  const isLoading = loading || loadingScheduled || details.isSendFromDataLoading

  useEffect(() => {
    if (items.length) void details.loadAllSendFromInfo(items)
  }, [items, details])

  const rows = useMemo(() => {
    return items.map((item) => {
      item.setActionsProps({
        actions: unEnrollActions(item, () => details.refresh()),
      })

      return item
    })
  }, [items])

  return (
    <section className={styles.table}>
      {isEmpty ? (
        <EmptyState text={'No contacts found'} />
      ) : (
        <Table
          ariaLabel={'ChatbotAnalyticTable'}
          columns={columns}
          rows={rows}
          initPage={page}
          limit={limit}
          isLoading={isLoading}
          rowsCount={total}
          onChangePagination={changePaging}
          startPaginationSize={10}
          virtualization
        />
      )}
    </section>
  )
})
